import { useTheme } from 'styled-components';

import { roundDecimal } from '@libs/helpers';
import { SelectData, SelectValue, Typography } from '@libs/components';
import { CurrencyInputProps } from '@libs/components/src/currency-input/CurrencyInput';

import { useTranslation } from 'src/hooks';
import {
  BalanceSelectorsWrapper,
  ContentContainer,
  CustomCurrencyInput,
  PercentButton,
} from './styles';

type CurrencyInputWithBalanceProps<
  V extends SelectValue,
  T extends SelectData<V>
> = CurrencyInputProps<V, T> & {
  balanceAmount?: number;
  setInputValue?: (newVal: string) => void;
  isBalanceSelectVisible?: boolean;
};

const CurrencyInputWithBalanceOptions = <
  V extends SelectValue,
  T extends SelectData<V>
>({
  balanceAmount,
  setInputValue,
  isBalanceSelectVisible,
  className,
  ...props
}: CurrencyInputWithBalanceProps<V, T>) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const handleBalanceButtonClick = (percentage: number) => {
    if (balanceAmount) {
      const roundedAmount = props.roundingPrecision
        ? roundDecimal(balanceAmount * percentage, props.roundingPrecision)
        : balanceAmount * percentage;

      setInputValue?.(roundedAmount.toString());
    }
  };

  return (
    <ContentContainer className={className}>
      <CustomCurrencyInput {...props} />

      {isBalanceSelectVisible && (
        <BalanceSelectorsWrapper>
          <PercentButton
            size="sm"
            title="25%"
            buttonType="secondary"
            onClick={handleBalanceButtonClick.bind(null, 0.25)}
          />
          <PercentButton
            size="sm"
            title="50%"
            buttonType="secondary"
            onClick={handleBalanceButtonClick.bind(null, 0.5)}
          />
          <PercentButton
            size="sm"
            title="75%"
            buttonType="secondary"
            onClick={handleBalanceButtonClick.bind(null, 0.75)}
          />
          <PercentButton
            size="sm"
            buttonType="secondary"
            onClick={handleBalanceButtonClick.bind(null, 1)}
            className="max-button"
          >
            <Typography
              type="bodyButton"
              fontFamily="primaryMedium"
              color={colors.borders.highlight}
            >
              {t('common_max')}
            </Typography>
          </PercentButton>
        </BalanceSelectorsWrapper>
      )}
    </ContentContainer>
  );
};

export default CurrencyInputWithBalanceOptions;
